import {
  createPeriodItem,
  generatePeriodDays,
  type CalendarItem,
} from "../calendar.types";
import { ageYearsFromDob, mapPeriodEvidence } from "./evidenceMapper";
import { calendarDayOf } from "./menstrualBleeding";
import { getFertileWindowPrediction } from "./orchestrator";
import { predictionToCalendarItems } from "./predictionToCalendarItems";
import type { FertileWindowPrediction, LocalDate } from "./fertileWindow.types";

export type PeriodRangeInput = {
  start: string | null | undefined;
  end: string | null | undefined;
};

export type FertileWindowCalendarData = {
  prediction: FertileWindowPrediction;
  markersByDate: Map<string, ("period" | "fertile")[]>;
};

/**
 * The calendar day a value refers to.
 *
 * Shares `calendarDayOf` with the bleeding mapper rather than splitting on "T"
 * again: these inputs are plain dates today, but the one place that assumed an
 * ISO separator silently discarded every Postgres timestamp it was handed.
 */
const dateOnly = calendarDayOf;

/** Build period and fertile-window markers for the live Care calendar. */
export function buildFertileWindowCalendarData({
  periodRanges,
  loggedPeriodDays = [],
  dateOfBirth,
  declaredCycleLengthDays,
  today,
}: {
  periodRanges: PeriodRangeInput[];
  /**
   * Single days the patient marked as bleeding — from the period log
   * (`/patients/{patientId}/periods`) and from the Care check-in, whose
   * bleeding levels are stored as symptom rows.
   *
   * Kept separate from `periodRanges` because the two shapes mean different
   * things. A medical-history entry is a remembered period with a start and
   * maybe an end, so an absent end is filled in with the usual length. A
   * marked day is one day, and nothing may be assumed on either side of it.
   * `extractPeriodBlocks` then groups consecutive days into one period, which
   * is what makes a run of marked days count as a single cycle start instead
   * of one start per day.
   */
  loggedPeriodDays?: readonly (string | null | undefined)[];
  dateOfBirth?: string | null;
  declaredCycleLengthDays?: {
    min: number | null;
    max: number | null;
  } | null;
  today: LocalDate;
}): FertileWindowCalendarData {
  const observedPeriodItems: CalendarItem[] = [];
  const markersByDate = new Map<string, ("period" | "fertile")[]>();
  const addMarker = (date: string, marker: "period" | "fertile") => {
    const existing = markersByDate.get(date);
    if (existing) {
      if (!existing.includes(marker)) existing.push(marker);
      return;
    }
    markersByDate.set(date, [marker]);
  };

  const seenPeriodDays = new Set<string>();
  const addPeriodDay = (date: string) => {
    if (seenPeriodDays.has(date)) return;
    seenPeriodDays.add(date);
    observedPeriodItems.push(createPeriodItem(date));
    addMarker(date, "period");
  };

  // Marked days first: they are what the patient recorded herself, and a
  // medical-history range that overlaps one must not turn a single period into
  // two observations.
  for (const logged of loggedPeriodDays) {
    const date = dateOnly(logged);
    if (date) addPeriodDay(date);
  }

  for (const range of periodRanges) {
    const start = dateOnly(range.start);
    if (!start) continue;
    const end = dateOnly(range.end);
    for (const date of generatePeriodDays(start, end)) {
      addPeriodDay(date);
    }
  }

  const prediction = getFertileWindowPrediction(
    mapPeriodEvidence({
      items: observedPeriodItems,
      ageYears: ageYearsFromDob(dateOfBirth),
      declaredCycleLengthDays: declaredCycleLengthDays ?? null,
    }),
    today,
  );

  for (const item of predictionToCalendarItems(prediction, today)) {
    const date = dateOnly(item.startAt);
    if (!date) continue;
    addMarker(date, item.type === "fertile" ? "fertile" : "period");
  }

  return { prediction, markersByDate };
}
